import { Link } from 'react-router-dom';
import {
  CROP_ASPECTS,
  cropSizeFor,
  IMAGE_VARIANTS,
  initials,
  type CropAspect,
  type ImageVariant,
  type PersonPortrait,
  type PersonRef,
} from '@familienbaum/shared';
import { Avatar } from '@/components/ui/misc';
import { FaceCrop } from '@/components/media/face-crop';
import { cn } from '@/lib/utils';

/** Alles, was zum Zeichnen eines Gesichts nötig ist – nie die ganze Person. */
type Portrayed = Pick<PersonRef, 'displayName'> & { portrait?: PersonPortrait | null };

const AVATAR_PX = { xs: 24, sm: 32, md: 40, lg: 56, xl: 96 } as const;
const AVATAR_CLASS = {
  xs: 'size-6 text-[10px]',
  sm: 'size-8 text-[11px]',
  md: 'size-10 text-[13px]',
  lg: 'size-14 text-base',
  xl: 'size-24 text-2xl',
} as const;

const VARIANT_ORDER: ImageVariant[] = ['thumb', 'medium', 'large'];

/** Das nächstgelegene Format, das der Server anbietet. */
function snapAspect(aspect: number): CropAspect {
  return CROP_ASPECTS.reduce((best, candidate) =>
    Math.abs(candidate - aspect) < Math.abs(best - aspect) ? candidate : best,
  );
}

/**
 * Die Adresse des fertig geschnittenen Gesichts.
 *
 * Vorher lud der Browser das ganze Foto und rückte es per CSS zurecht – für
 * einen 24px-Avatar hieß das im Bestand bis zu zwei Megabyte, und weil auf
 * `/api/*` `no-store` liegt, bei jedem Seitenaufruf erneut. Vor allem aber:
 * bei einem Gruppenbild kamen mit den Bytes alle anderen darauf mit. Genau
 * das trennt der serverseitige Ausschnitt.
 *
 * `null`, wenn es nichts zu schneiden gibt – dann ist das Porträt ohnehin ein
 * ganzes Bild und es bleibt bei `portraitSource`.
 */
export function portraitCropUrl(
  portrait: PersonPortrait,
  renderedPx: number,
  aspect = 1,
): string | null {
  if (!portrait.regionId) return null;
  const size = cropSizeFor(renderedPx);
  return `/api/media/${portrait.mediaId}/crop/${portrait.regionId}?size=${size}&aspect=${snapAspect(aspect)}`;
}

/**
 * Die kleinste Bildvariante, die für diese Anzeigegröße noch scharf ist.
 *
 * Entscheidend ist nicht die Anzeigefläche allein, sondern wie viel vom Bild
 * überhaupt zu sehen ist: bei einem Gesicht in einer Gruppe sind das im
 * Bestand bis herunter zu 0,8 % der Fläche – also rund 9 % je Kante. Ein
 * 320px-Vorschaubild liefert davon 29 Pixel, und die auf eine 150px-Kachel
 * gezogen sind Brei. Umgekehrt wäre es Verschwendung, für einen 24px-Avatar
 * ein 2048px-Bild zu laden. Also beides ausrechnen statt eines zu raten.
 *
 * Der Faktor 1.3 ist Reserve für Bildschirme mit doppelter Pixeldichte. Er ist
 * bewusst kein glattes 2: an den echten Zahlen dieses Bestands nachgerechnet
 * kippt schon 1.5 die meisten Kacheln von `medium` (1024) auf `large` (2048) –
 * für ein sichtbares Plus, das den doppelten Download nicht wert ist. Bei 1.3
 * liefert `medium` für eine 260px-Kachel noch rund die 1,3-fache Auflösung,
 * und `large` bleibt den wirklich kleinen Ausschnitten vorbehalten.
 */
export function portraitSource(portrait: PersonPortrait, renderedPx: number): string | undefined {
  const share = portrait.box
    ? // `padBox` legt 30 % Luft um die Markierung, das vergrößert den
      // sichtbaren Anteil. Die längere Kante entscheidet.
      Math.min(1, Math.max(portrait.box.width, portrait.box.height) * 1.3)
    : 1;
  const needed = (renderedPx * 1.3) / Math.max(share, 0.02);

  for (const variant of VARIANT_ORDER) {
    const url = portrait.urls[variant];
    if (url && IMAGE_VARIANTS[variant].width >= needed) return url;
  }
  // Nichts ist groß genug – dann das Größte, was da ist.
  return (
    portrait.urls.large ?? portrait.urls.medium ?? portrait.urls.thumb ?? portrait.urls.original
  );
}

/**
 * Rundes Gesicht für den Textfluss – Tabelle, Chip, Menü, Zeile.
 *
 * Der Kreis ist ein Kennzeichen, kein Bild zum Ansehen: er darf beschnitten
 * sein. Trägt das Porträt eine Markierung, wird trotzdem darauf zugeschnitten
 * statt auf die Bildmitte – bei einem Gruppenbild ist die Mitte selten die
 * gemeinte Person.
 */
export function PersonAvatar({
  person,
  size = 'md',
  className,
}: {
  person: Portrayed;
  size?: keyof typeof AVATAR_PX;
  className?: string;
}) {
  const portrait = person.portrait ?? null;

  // Der Server liefert den Ausschnitt bereits quadratisch – nichts mehr zu
  // rücken, und `Avatar` bringt Rahmen und Monogramm-Rückfall gleich mit.
  const cropped = portrait ? portraitCropUrl(portrait, AVATAR_PX[size]) : null;

  return (
    <Avatar
      src={cropped ?? (portrait ? (portraitSource(portrait, AVATAR_PX[size]) ?? null) : null)}
      alt={person.displayName}
      fallback={initials(person.displayName)}
      size={size}
      className={className}
    />
  );
}

/**
 * Rechteckiges Porträt – Kachel, Baumknoten, Kopf der Detailseite.
 *
 * Hier ist das Bild der Inhalt, nicht die Beschriftung daneben. 4:5 ist das
 * Format der Abzüge, aus denen dieses Archiv besteht; ein hochkanter Rahmen
 * nimmt beim Zuschnitt außerdem Stirn und Schultern mit, statt das Gesicht
 * quadratisch auszustanzen.
 *
 * Ohne Bild bleibt das Monogramm – aber im selben Rahmen, damit ein Raster
 * nicht springt.
 */
export function PersonPortraitFrame({
  person,
  px,
  aspect = 0.8,
  className,
}: {
  person: Portrayed;
  /** Ungefähre Anzeigebreite in Pixeln – wählt die Bildvariante. */
  px: number;
  aspect?: number;
  className?: string;
}) {
  const portrait = person.portrait ?? null;
  const src = portrait ? portraitSource(portrait, px) : undefined;
  const frame = cn('relative overflow-hidden bg-sunken', className);

  const cropped = portrait ? portraitCropUrl(portrait, px, aspect) : null;
  if (cropped) {
    return (
      <div className={frame}>
        <img
          src={cropped}
          alt={person.displayName}
          loading="lazy"
          draggable={false}
          className="size-full object-cover"
        />
      </div>
    );
  }

  // Rückfall für Porträts ohne Markierung: ein gesetztes Bild ohne Gesicht
  // darauf. Da gibt es nichts zu schneiden, also weiter wie bisher.
  if (portrait?.box && src) {
    return (
      <FaceCrop
        src={src}
        region={portrait.box}
        alt={person.displayName}
        aspect={aspect}
        imageWidth={portrait.imageWidth}
        imageHeight={portrait.imageHeight}
        className={frame}
      />
    );
  }

  if (src) {
    return (
      <div className={frame}>
        <img
          src={src}
          alt={person.displayName}
          loading="lazy"
          draggable={false}
          className="size-full object-cover"
        />
      </div>
    );
  }

  // Die Schriftgröße hängt an der *gemessenen* Rahmenbreite, nicht an `px`:
  // `px` ist ein Hinweis für die Variantenwahl und sagt nichts darüber, wie
  // breit die Kachel am Ende wirklich ist – im Raster hängt das an der
  // Spaltenzahl. `cqw` misst den Container selbst, damit stimmt es in der
  // 54px-Baumkarte genauso wie im 288px-Kartenkopf.
  return (
    <div
      className={cn(
        frame,
        '@container flex items-center justify-center',
        // Monogramm in der Serife: es steht an der Stelle eines Gesichts und
        // soll wie eine Beschriftung wirken, nicht wie ein Platzhalter aus
        // einer Verwaltungsmaske.
        'fb-blank font-display font-medium text-accent-strong',
      )}
      aria-hidden
    >
      <span className="text-[22cqw] leading-none">{initials(person.displayName)}</span>
    </div>
  );
}

/** Avatar + Name + Lebensdaten, überall gleich – klickbar zur Detailseite. */
export function PersonChip({
  person,
  sublabel,
  size = 'sm',
  as = 'link',
  className,
}: {
  person: PersonRef;
  sublabel?: React.ReactNode;
  size?: 'xs' | 'sm' | 'md';
  as?: 'link' | 'plain';
  className?: string;
}) {
  const lifespan =
    person.birthYear || person.deathYear
      ? `${person.birthYear ?? ''}–${person.deathYear ?? ''}`.replace(/^–/, '† ')
      : null;

  const content = (
    <span className={cn('flex min-w-0 items-center gap-2.5', className)}>
      <PersonAvatar person={person} size={size} />
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium text-ink">{person.displayName}</span>
        {sublabel ?? (lifespan ? <span className="block truncate text-[12px] text-ink-3 tabular">{lifespan}</span> : null)}
      </span>
    </span>
  );

  if (as === 'plain') return content;

  return (
    <Link
      to={`/personen/${person.id}`}
      className="inline-flex min-w-0 rounded-full transition-opacity hover:opacity-80"
    >
      {content}
    </Link>
  );
}
